import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Blog_Subscribers_API, Career_Subscribers_API } from "./environment";
import { useAuth } from "../context/AuthContext";
import { FaTrash, FaFilter } from "react-icons/fa";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";

// Component for managing subscribers with get, delete, multiple delete, and filter functionality
export default function Subscribers() {
  const { userData } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    id: [],
    name: [],
    email: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [activeTab, setActiveTab] = useState("blog"); // Default to Blog Subscribers

  const columnDisplay = {
    id: "ID",
    name: "Name",
    email: "Email",
  };

  // Get distinct values for a column
  const getDistinctValues = (column) => {
    const values = subscribers.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort((a, b) =>
      column === "id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply filters to an item
  const applyFilters = (item) => {
    return (
      (filters.id.length === 0 || filters.id.includes(item.id)) &&
      (filters.name.length === 0 || filters.name.includes(item.name)) &&
      (filters.email.length === 0 || filters.email.includes(item.email))
    );
  };

  // Filter subscribers
  const filteredSubscribers = subscribers.filter(applyFilters);

  const totalPages = Math.ceil(filteredSubscribers.length / itemsPerPage);
  const paginatedSubscribers = filteredSubscribers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Fetch subscribers based on active tab
  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        const apiUrl =
          activeTab === "blog"
            ? `${Blog_Subscribers_API}/getAll`
            : `${Career_Subscribers_API}/getAll`;
        const response = await fetch(apiUrl, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch ${activeTab} subscribers`);
        }
        const data = await response.json();
        setSubscribers(data);
        setSelectedRows([]); // Reset selected rows on tab switch
        setCurrentPage(1); // Reset to first page
        setFilters({ id: [], name: [], email: [] }); // Reset filters
      } catch (error) {
        console.error(`Error fetching ${activeTab} subscribers:`, error);
        Swal.fire(
          "Error",
          `Failed to fetch ${activeTab} subscribers. Please try again.`,
          "error"
        );
      }
    };
    fetchSubscribers();
  }, [userData.access_token, activeTab]);

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  // Select all filtered subscribers
  const handleSelectAll = () => {
    if (selectedRows.length === filteredSubscribers.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredSubscribers.map((item) => item.id));
    }
  };

  // Handle filter click for a column
  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  // Apply filter selections
  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [showFilterPopup]: newSelected,
    }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete a single subscriber
  const deleteSubscriber = async (id) => {
    Swal.fire({
      text: `Do you want to delete this ${activeTab} subscriber?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const apiUrl =
            activeTab === "blog"
              ? `${Blog_Subscribers_API}/delete/${id}`
              : `${Career_Subscribers_API}/delete/${id}`;
          const response = await fetch(apiUrl, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${userData.access_token}`,
              Accept: "application/json",
            },
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error("Server Error:", errorText);
            throw new Error(`Failed to delete ${activeTab} subscriber`);
          }

          const updatedSubscribers = subscribers.filter((sub) => sub.id !== id);
          setSubscribers(updatedSubscribers);
          setSelectedRows(selectedRows.filter((rowId) => rowId !== id));

          Swal.fire(
            "Success!",
            `${
              activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
            } subscriber deleted successfully`,
            "success"
          );
        } catch (error) {
          console.error(`Error deleting ${activeTab} subscriber:`, error);
          Swal.fire(
            "Error",
            `Failed to delete ${activeTab} subscriber. Please try again.`,
            "error"
          );
        }
      }
    });
  };

  // Delete multiple selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    Swal.fire({
      text: `Do you want to delete ${selectedRows.length} selected ${activeTab} subscribers?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const apiUrl =
            activeTab === "blog"
              ? `${Blog_Subscribers_API}/delete`
              : `${Career_Subscribers_API}/delete`;
          await Promise.all(
            selectedRows.map(async (id) => {
              const response = await fetch(`${apiUrl}/${id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${userData.access_token}`,
                  Accept: "application/json",
                },
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error("Server Error:", errorText);
                throw new Error(
                  `Failed to delete ${activeTab} subscriber ${id}`
                );
              }
            })
          );

          const updatedSubscribers = subscribers.filter(
            (sub) => !selectedRows.includes(sub.id)
          );
          setSubscribers(updatedSubscribers);
          setSelectedRows([]);

          Swal.fire(
            "Success!",
            `Selected ${activeTab} subscribers deleted successfully`,
            "success"
          );
        } catch (error) {
          console.error(`Error deleting ${activeTab} subscribers:`, error);
          Swal.fire(
            "Error",
            `Failed to delete some ${activeTab} subscribers. Please try again.`,
            "error"
          );
        }
      }
    });
  };

  // Toggle visibility of all checkboxes in the popup
  const handleToggleVisible = (e) => {
    const distinct = getDistinctValues(showFilterPopup);
    if (e.target.checked) {
      setTempSelected(distinct);
    } else {
      setTempSelected([]);
    }
  };

  // Render filter popup
  const renderFilterPopup = (column) => {
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter((val) =>
      val?.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) =>
      tempSelected.includes(val)
    );

    const clearSelections = () => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [column]: [],
      }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    return (
      <div className="filter-popup">
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={handleToggleVisible}
          />
          <input
            className="filter-search"
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <span className="clear-search" onClick={() => setSearchInput("")}>
              ×
            </span>
          )}
        </div>
        <div className="suggestions-list">
          {filteredValues.length === 0 ? (
            <div>No results found</div>
          ) : (
            filteredValues.map((val) => (
              <label key={val?.toString() || "null"}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(val)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setTempSelected([...tempSelected, val]);
                    } else {
                      setTempSelected(tempSelected.filter((v) => v !== val));
                    }
                  }}
                />
                {val != null ? val.toString() : "N/A"}
              </label>
            ))
          )}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>Subscribers - SMTS Admin Panel</title>
      </Helmet>
      <div className="page-title">
        <h2
          className={activeTab === "blog" ? "active" : ""}
          onClick={() => setActiveTab("blog")}
          style={{
            cursor: "pointer",
            display: "inline-block",
            marginRight: "20px",
          }}
        >
          Blog Subscribers
        </h2>
        <h2
          className={activeTab === "career" ? "active" : ""}
          onClick={() => setActiveTab("career")}
          style={{ cursor: "pointer", display: "inline-block" }}
        >
          Career Subscribers
        </h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredSubscribers.length &&
                    filteredSubscribers.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                {hasActiveFilters && (
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === filteredSubscribers.length &&
                      filteredSubscribers.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                )}
                ID
                <FaFilter
                  className={`filter-icon ${
                    filters.id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "id" && renderFilterPopup("id")}
              </th>
              <th style={{ position: "relative" }}>
                Name
                <FaFilter
                  className={`filter-icon ${
                    filters.name.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("name")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "name" && renderFilterPopup("name")}
              </th>
              <th style={{ position: "relative" }}>
                Email
                <FaFilter
                  className={`filter-icon ${
                    filters.email.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("email")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "email" && renderFilterPopup("email")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedSubscribers.map((sub) => (
              <tr
                key={sub.id}
                className={selectedRows.includes(sub.id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(sub.id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(sub.id)}
                    onChange={() => toggleRowSelection(sub.id)}
                  />
                </td>
                <td>{sub.id}</td>
                <td>{sub.name}</td>
                <td>{sub.email}</td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteSubscriber(sub.id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" title="Delete Selected" /> (
          {selectedRows.length})
        </button>
      )}
    </div>
  );
}
